
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react"; 

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const navLinks = [
    { name: "Architecture", href: "#architecture" },
    { name: "Pricing", href: "#pricing" },
    { name: "Road Map", href: "#roadmap" },
  ];
  const isLight = scrolled;
  // 1. Logic xử lý đổi style khi scroll (Glassmorphism)
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-40% 0px -40% 0px", 
      threshold: 0,
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(`#${entry.target.id}`);
          }
        });
      };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    // Theo dõi tất cả các section có id tương ứng với navLinks
    navLinks.forEach((link) => {
      const element = document.querySelector(link.href);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? "py-3 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 shadow-2xl" 
          : "py-6 bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        
        {/* LOGO */}
       
<a href="/" className="group relative flex items-center gap-3 no-underline">
  <div className="relative flex items-center justify-center">
    <div
      className={`w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center rotate-3 group-hover:rotate-0 transition-all duration-500 ${
        isLight
          ? "bg-blue-600 shadow-[0_0_10px_rgba(59,130,246,0.2)]"
          : "bg-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.5)]"
      }`}
    >
      <img
        src="/images/dm-logo.png"
        alt="Logo"
        className="w-full h-full object-cover"
      />
    </div>

    {/* Glow background chỉ mạnh khi hero mode */}
    <div
      className={`absolute inset-0 bg-blue-500 blur-lg transition-opacity duration-500 ${
        isLight ? "opacity-10" : "opacity-40"
      }`}
    />
  </div>

  <div className="flex flex-col">
    <div className="flex items-center">
      <span
        className={`font-display font-black text-xl tracking-tighter leading-none transition-colors duration-300 ${
          isLight ?  "text-white" : "text-slate-900" 
        }`}
      >
        GAO
      </span>

      <span
        className={`ml-1 w-1 h-1 rounded-full animate-pulse transition-colors duration-300 ${
          isLight ? "bg-blue-600" : "bg-blue-500"
        }`}
      />
    </div>

    <span
      className={`text-[10px] font-mono font-bold tracking-[0.3em] uppercase transition-colors duration-300 ${
        isLight ? "text-blue-600" : "text-blue-500"
      }`}
    >
      Workspace
    </span>
  </div>
</a>
        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-2">
          {navLinks.map((link) => {
            const isActive = activeSection === link.href;
            return (
              
              <a
  key={link.name}
  href={link.href}
  className={`relative px-5 py-2 text-[11px] font-bold uppercase tracking-widest transition-all duration-300 rounded-full ${
    isActive
      ? isLight
        ? "text-blue-600 bg-blue-500/10 shadow-[0_0_10px_rgba(59,130,246,0.15)]"
        : "text-blue-400 bg-blue-500/10 shadow-[0_0_15px_rgba(59,130,246,0.1)]"
      : isLight
        ? "text-neutral-400 hover:text-white hover:bg-white/5" : "text-neutral-600 hover:text-slate-900 hover:bg-black/5"
  }`}
>
                {link.name}
                {/* Thanh line nhỏ dưới link khi active */}
                {isActive && (
                  <motion.div 
                    layoutId="activeTab"
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px] bg-blue-500 rounded-full"
                  />
                )}
              </a>
            );
          })}
          
          <div className="ml-4 h-4 w-[1px] bg-white/10 mx-2" />
          <button  onClick={() => window.location.href = "https://gao.systems/en"} className="ml-2 px-6 py-2.5 rounded-full bg-white text-black font-black text-[10px] uppercase tracking-[0.15em] hover:bg-neutral-200 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] active:scale-95 transition-all">
            Get Started
          </button>
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setOpen(!open)} className="md:hidden p-2 text-neutral-400 hover:text-white transition-colors">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#050505] border-b border-white/5 overflow-hidden shadow-2xl"
          >
            <div className="container mx-auto px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className={`text-2xl font-bold tracking-tighter transition-colors ${
                    activeSection === link.href ? "text-blue-500" : "text-neutral-400"
                  }`}
                >
                  {link.name}
                </a>
              ))}
              <button onClick={() => window.location.href = "https://gao.systems/en"} className="mt-4 px-5 py-4 rounded-2xl bg-white text-black font-bold uppercase tracking-widest text-sm">
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Header;